import { useUser } from "@/api/auth.ts";
import { useGetInstance } from "@/api/instance.ts";
import { getRegion, REGIONS, tokenKeyFor } from "@/config/regions";
import {
  clearTranslationLocalStorageCache,
  fetchMainLanguage,
  SupportedLocale,
} from "@/i18n.ts";
import { migrateLegacyTokens } from "@/lib/auth-tokens";
import {
  LoginResponse,
  LoginResponseDTO,
  NO_BACKOFFICE_ACCESS_CODE,
} from "@/lib/auth/auth-types";
import { getTokenForRegion, RegionCode } from "@/lib/axiosConfig";
import { queryClient } from "@/lib/queryClient";
import {
  clearBoPreferencesCache,
  hydrateBoPreferencesFromServer,
  migrateLocalBoPreferencesToServer,
} from "@/lib/user-preferences";
import axios, { HttpStatusCode } from "axios";
import { ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AuthContext,
  MultiRegionLoginResult,
  RegionTokens,
} from "./AuthContext";

interface AuthProviderProps {
  children: ReactNode;
}

/** Région ayant déclenché le 2FA (une seule pour éviter N emails / N codes). */
export const PENDING_2FA_REGION_KEY = "pending2FARegion";

/** Mot de passe conservé le temps du flux 2FA pour répliquer la confiance multi-régions. */
export const PENDING_2FA_PASSWORD_KEY = "pending2FAPassword";

interface DeviceTrustPayload {
  deviceId: string;
  lastUsedIp?: string;
  twoFactorLastUsedAt?: string;
}

function clearPending2FAFlow() {
  sessionStorage.removeItem(PENDING_2FA_REGION_KEY);
  sessionStorage.removeItem(PENDING_2FA_PASSWORD_KEY);
}

function extractDeviceTrust(
  response: LoginResponse,
): DeviceTrustPayload | null {
  if (response.status !== HttpStatusCode.Ok || !("token" in response)) {
    return null;
  }
  if (!response.deviceId) return null;
  return {
    deviceId: response.deviceId,
    lastUsedIp: response.lastUsedIp,
    twoFactorLastUsedAt: response.twoFactorLastUsedAt,
  };
}

async function syncDeviceTrustToRegion(
  baseUrl: string,
  email: string,
  password: string,
  trust: DeviceTrustPayload,
): Promise<void> {
  try {
    await axios.post(
      `${baseUrl}auth/sync-device-trust`,
      {
        email,
        password,
        deviceId: trust.deviceId,
        lastUsedIp: trust.lastUsedIp,
        twoFactorLastUsedAt: trust.twoFactorLastUsedAt,
      },
      { withCredentials: true },
    );
  } catch (err) {
    console.warn("[auth] device trust sync failed", baseUrl, err);
  }
}

async function replicateDeviceTrustToOtherRegions(
  sourceRegionCode: string,
  email: string,
  password: string,
  trust: DeviceTrustPayload,
): Promise<void> {
  await Promise.all(
    REGIONS.filter((region) => region.code !== sourceRegionCode).map((region) =>
      syncDeviceTrustToRegion(region.baseUrl, email, password, trust),
    ),
  );
}

async function collectTokensForMissingRegions(
  email: string,
  password: string,
  locale: SupportedLocale,
  tokens: RegionTokens,
): Promise<RegionTokens> {
  const updatedTokens = { ...tokens };

  for (const region of REGIONS) {
    if (updatedTokens[region.code]) continue;

    const result = await loginToRegion(
      region.baseUrl,
      { email, password },
      locale,
    );
    if (result.status === HttpStatusCode.Ok && "token" in result) {
      updatedTokens[region.code] = result.token;
    }
  }

  return updatedTokens;
}

/**
 * Après sync-device-trust, les SID des autres régions peuvent avoir été
 * régénérés (LocalAuthGuard.logIn). On force un nouveau login sur ces
 * régions pour stocker un token encore valide.
 */
async function refreshTokensAfterDeviceTrustSync(
  sourceRegionCode: string,
  email: string,
  password: string,
  locale: SupportedLocale,
  tokens: RegionTokens,
): Promise<RegionTokens> {
  const refreshed: RegionTokens = {};
  if (tokens[sourceRegionCode]) {
    refreshed[sourceRegionCode] = tokens[sourceRegionCode];
  }

  for (const region of REGIONS) {
    if (region.code === sourceRegionCode) continue;

    const result = await loginToRegion(
      region.baseUrl,
      { email, password },
      locale,
    );
    if (result.status === HttpStatusCode.Ok && "token" in result) {
      refreshed[region.code] = result.token;
    }
  }

  return refreshed;
}

function clearAuthSessionForNewLogin() {
  localStorage.removeItem("instance");
  queryClient.clear();
}

function clearStoredSession() {
  localStorage.removeItem("token");
  localStorage.removeItem("instance");
  localStorage.removeItem("region");
  for (const region of REGIONS) {
    localStorage.removeItem(tokenKeyFor(region.code));
  }
}

/**
 * Appelle /auth/signin sur une région. Utilise une instance axios anonyme
 * pour éviter que l'intercepteur principal n'écrase la baseURL pendant le
 * login parallèle.
 */
async function loginToRegion(
  baseUrl: string,
  payload: { email: string; password: string },
  locale: SupportedLocale,
): Promise<LoginResponse> {
  try {
    const { data, status } = await axios.post<LoginResponseDTO>(
      `${baseUrl}auth/signin?locale=${locale}`,
      payload,
      { withCredentials: true },
    );
    return { ...data, status } as LoginResponse;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status ?? HttpStatusCode.BadRequest;
      return {
        status,
        message:
          error.response?.data?.message ?? "Unknown error. Check server logs.",
        code: error.response?.data?.code,
      };
    }
    throw error;
  }
}

function isNoBackofficeAccess(response: LoginResponse | null): boolean {
  return (
    !!response &&
    response.status === HttpStatusCode.Forbidden &&
    "code" in response &&
    response.code === NO_BACKOFFICE_ACCESS_CODE
  );
}

async function twoFactorToRegion(
  baseUrl: string,
  payload: { email: string; twoFactorToken: string },
  locale: SupportedLocale,
): Promise<LoginResponse> {
  try {
    const { data, status } = await axios.post<LoginResponseDTO>(
      `${baseUrl}auth/2fa?locale=${locale}`,
      payload,
      { withCredentials: true },
    );
    return { ...data, status } as LoginResponse;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status ?? HttpStatusCode.BadRequest;
      return {
        status,
        message:
          error.response?.data?.message ?? "Unknown error. Check server logs.",
      };
    }
    throw error;
  }
}

function hasStoredSession(): boolean {
  return !!localStorage.getItem("token") && !!localStorage.getItem("instance");
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [token, setToken] = useState<string | null>(null);
  const [regionTokens, setRegionTokens] = useState<RegionTokens>({});
  const [instance, setInstanceState] = useState<string | undefined>(undefined);
  // When restoring a session, block dashboard until prefs are hydrated.
  const [boPreferencesReady, setBoPreferencesReady] = useState(
    () => !hasStoredSession(),
  );

  const navigate = useNavigate();
  const { data: user } = useUser(token);
  const { data: instanceData } = useGetInstance(instance);

  /**
   * Persiste les tokens obtenus par région, pose localStorage.region sur la
   * première région ayant répondu OK (sinon tout appel via l'axios principal
   * avant la sélection d'instance router vers la mauvaise région).
   */
  const persistTokens = (tokens: RegionTokens) => {
    let primaryCode: RegionCode | null = null;
    let primaryToken: string | null = null;

    // Itère dans l'ordre de REGIONS pour que la priorité suive la config.
    // Les régions absentes de `tokens` voient leur clé purgée pour éviter
    // de réutiliser un SID mort au prochain chargement d'instances.
    for (const region of REGIONS) {
      const t = tokens[region.code];
      if (!t) {
        localStorage.removeItem(tokenKeyFor(region.code));
        continue;
      }
      localStorage.setItem(tokenKeyFor(region.code), t);
      if (!primaryCode) {
        primaryCode = region.code;
        primaryToken = t;
      }
    }

    if (primaryCode && primaryToken) {
      localStorage.setItem("region", primaryCode);
      localStorage.setItem("token", primaryToken);
      setToken(primaryToken);
    }

    setRegionTokens(tokens);
  };

  /**
   * Login séquentiel sur les régions configurées.
   * En cas de 2FA, on s'arrête à la première région qui répond 202, sauf si
   * une autre région a déjà authentifié l'utilisateur dans ce flux (cookie /
   * métadonnées 2FA indépendantes par host API). Dans ce cas on ignore le 202
   * pour éviter un code à chaque reconnexion.
   */
  const handleLogin = async (
    email: string,
    password: string,
    locale: SupportedLocale,
  ): Promise<MultiRegionLoginResult> => {
    clearPending2FAFlow();

    const newTokens: RegionTokens = {};
    let primaryResponse: LoginResponse | null = null;
    let hasAuthenticatedRegion = false;
    let sourceRegionCode: string | null = null;

    for (const region of REGIONS) {
      let result: LoginResponse;
      try {
        result = await loginToRegion(
          region.baseUrl,
          { email, password },
          locale,
        );
      } catch {
        if (!primaryResponse) {
          primaryResponse = {
            status: HttpStatusCode.ServiceUnavailable,
            message: "Region unavailable.",
          };
        }
        continue;
      }

      if (result.status === HttpStatusCode.Ok && "token" in result) {
        newTokens[region.code] = result.token;
        hasAuthenticatedRegion = true;
        if (!sourceRegionCode) sourceRegionCode = region.code;
        if (!primaryResponse || primaryResponse.status !== HttpStatusCode.Ok) {
          primaryResponse = result;
        }
        continue;
      }

      if (result.status === HttpStatusCode.Accepted) {
        if (hasAuthenticatedRegion) {
          continue;
        }
        sessionStorage.setItem(PENDING_2FA_REGION_KEY, region.code);
        sessionStorage.setItem(PENDING_2FA_PASSWORD_KEY, password);
        primaryResponse = result;
        break;
      }

      // Mot de passe valide mais compte `customer` : ce refus est plus parlant
      // qu'un 401 d'une région où le compte n'existe pas (jamais au-dessus
      // d'une région qui a authentifié).
      if (
        !primaryResponse ||
        (primaryResponse.status !== HttpStatusCode.Ok &&
          isNoBackofficeAccess(result))
      ) {
        primaryResponse = result;
      }
    }

    if (primaryResponse === null) {
      primaryResponse = {
        status: HttpStatusCode.ServiceUnavailable,
        message: "All regions are unavailable.",
      };
    }

    let tokensToPersist = newTokens;

    if (!sessionStorage.getItem(PENDING_2FA_REGION_KEY)) {
      if (!hasAuthenticatedRegion) {
        // Login refusé partout : on vide la session précédente au lieu de la
        // laisser rouvrir /instance avec le jeton d'un autre compte.
        clearStoredSession();
        queryClient.clear();
        setToken(null);
        setRegionTokens({});
        setInstanceState(undefined);
        return { primaryResponse, tokens: {} };
      }

      const trust = primaryResponse && extractDeviceTrust(primaryResponse);
      if (trust && sourceRegionCode) {
        await replicateDeviceTrustToOtherRegions(
          sourceRegionCode,
          email,
          password,
          trust,
        );
        // sync-device-trust peut invalider les SID déjà obtenus : on
        // rafraîchit les tokens des autres régions après la sync.
        tokensToPersist = await refreshTokensAfterDeviceTrustSync(
          sourceRegionCode,
          email,
          password,
          locale,
          newTokens,
        );
        tokensToPersist = await collectTokensForMissingRegions(
          email,
          password,
          locale,
          tokensToPersist,
        );
      }

      clearAuthSessionForNewLogin();
      setInstanceState(undefined);
      persistTokens(tokensToPersist);
    }

    return { primaryResponse, tokens: tokensToPersist };
  };

  const handleTwoFactorLogin = async (
    email: string,
    twoFactorToken: string,
    locale: SupportedLocale,
  ): Promise<LoginResponse> => {
    const pendingCode = sessionStorage.getItem(PENDING_2FA_REGION_KEY);
    const region =
      getRegion(pendingCode) ??
      getRegion(localStorage.getItem("region")) ??
      REGIONS[0];

    const primaryResponse = await twoFactorToRegion(
      region.baseUrl,
      { email, twoFactorToken },
      locale,
    );

    if (
      primaryResponse.status === HttpStatusCode.Ok &&
      "token" in primaryResponse
    ) {
      const password = sessionStorage.getItem(PENDING_2FA_PASSWORD_KEY);
      const trust = extractDeviceTrust(primaryResponse);
      let tokens: RegionTokens = { [region.code]: primaryResponse.token };

      if (password && trust) {
        await replicateDeviceTrustToOtherRegions(
          region.code,
          email,
          password,
          trust,
        );
        tokens = await refreshTokensAfterDeviceTrustSync(
          region.code,
          email,
          password,
          locale,
          tokens,
        );
        tokens = await collectTokensForMissingRegions(
          email,
          password,
          locale,
          tokens,
        );
      }

      clearAuthSessionForNewLogin();
      setInstanceState(undefined);
      persistTokens(tokens);
      clearPending2FAFlow();
    }

    return primaryResponse;
  };

  useEffect(() => {
    const storedInstance = localStorage.getItem("instance");
    if (user && !storedInstance) {
      navigate("/instance");
    }
  }, [user, navigate]);

  useEffect(() => {
    const restoredTokens = migrateLegacyTokens();
    const storedToken = localStorage.getItem("token");
    const storedInstance = localStorage.getItem("instance");

    if (storedInstance) setInstanceState(storedInstance);
    if (storedToken) setToken(storedToken);
    if (Object.keys(restoredTokens).length > 0) setRegionTokens(restoredTokens);

    if (storedToken && storedInstance) {
      void (async () => {
        try {
          await migrateLocalBoPreferencesToServer(storedInstance);
          await hydrateBoPreferencesFromServer(storedInstance);
        } catch (err) {
          console.error("[auth] preference sync on restore failed", err);
        } finally {
          setBoPreferencesReady(true);
        }
      })();
    } else {
      setBoPreferencesReady(true);
    }
  }, []);

  /**
   * Sélectionne une instance et aligne le token actif sur la région donnée.
   *
   * Purge les tokens des régions non-choisies : une fois l'instance sélectionnée,
   * changer de région implique un logout/login (pas de switch d'instance depuis
   * le dashboard), donc ces tokens sont morts. Les garder en localStorage
   * n'apporte rien et augmente la surface d'exposition à un XSS.
   */
  const setInstance = (instanceName: string, regionCode?: RegionCode) => {
    setBoPreferencesReady(false);
    setInstanceState(instanceName);
    localStorage.setItem("instance", instanceName);
    void (async () => {
      try {
        await migrateLocalBoPreferencesToServer(instanceName);
        await hydrateBoPreferencesFromServer(instanceName);
      } catch (err) {
        console.error("[auth] preference sync on setInstance failed", err);
      } finally {
        setBoPreferencesReady(true);
      }
    })();
    if (!regionCode) return;

    localStorage.setItem("region", regionCode);
    const regionToken = getTokenForRegion(regionCode);
    if (regionToken) {
      setToken(regionToken);
      localStorage.setItem("token", regionToken);
    }

    for (const r of REGIONS) {
      if (r.code !== regionCode) localStorage.removeItem(tokenKeyFor(r.code));
    }
    setRegionTokens(regionToken ? { [regionCode]: regionToken } : {});
  };

  const logout = () => {
    clearBoPreferencesCache();
    clearTranslationLocalStorageCache();
    queryClient.clear();
    clearPending2FAFlow();
    setToken(null);
    setRegionTokens({});
    setInstanceState(undefined);
    setBoPreferencesReady(true);
    clearStoredSession();
    navigate("/login");
  };

  useEffect(() => {
    if (token && instance) {
      fetchMainLanguage();
    }
  }, [token, instance]);

  const checkToken = () => !!localStorage.getItem("token");

  const checkInstanceAndToken = () =>
    !!localStorage.getItem("token") && !!localStorage.getItem("instance");

  return (
    <AuthContext.Provider
      value={{
        token,
        regionTokens,
        instance,
        instanceData,
        setInstance,
        login: handleLogin,
        twoFactorLogin: handleTwoFactorLogin,
        logout,
        checkToken,
        checkInstanceAndToken,
        boPreferencesReady,
        user: user ? user[0] : null,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
